"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ParcelClient = void 0;
const base_client_1 = require("@deliverr/base-client");
const client_auth_1 = require("@deliverr/client-auth");
const core_auth_1 = require("@deliverr/core-auth");
const commons_logs_1 = require("@deliverr/commons-logs");
class ParcelClient extends base_client_1.ApiClient {
    constructor(config) {
        const serviceUrl = config.baseURL || process.env.PARCEL_SERVICE_URL;
        //Initially AuthEngine will be UNKNOWN,
        //as we want consumer of package to set the AuthEngine
        let apiClientConfig = {
            //AuthEngine.UNKNOWN will never be set in ApiClient
            authEngine: core_auth_1.AuthEngine.UNKNOWN,
        };
        if (!serviceUrl) {
            commons_logs_1.log.warn("PARCEL_SERVICE_URL environment variable is not set");
        }
        if (config.authEngine === core_auth_1.AuthEngine.COGNITO_INTERNAL) {
            const tokenHandler = new client_auth_1.OAuthClientTokenBuilder(config);
            apiClientConfig = {
                authEngine: core_auth_1.AuthEngine.COGNITO_INTERNAL,
                tokenRefreshFn: tokenHandler.getTokenFn(),
            };
        }
        else {
            apiClientConfig = {
                authEngine: config.authEngine,
            };
        }
        apiClientConfig = {
            ...config,
            ...apiClientConfig,
        };
        super({
            ...apiClientConfig,
            baseURL: `${serviceUrl}`,
        });
    }
    async processTrackingUpdate(trackingCode, packageTrackingSummary, packageTrackingDetail) {
        const url = `/v2/parcel/process-tracking-update/${trackingCode}`;
        const requestBody = {
            packageTrackingSummary,
            packageTrackingDetail,
        };
        try {
            return await this.post(url, requestBody);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async createParcel(customerId, platformName, parcelRequest) {
        const headers = platformName
            ? {
                "x-customer-id": customerId,
                "x-created-by": platformName,
            }
            : {
                "x-customer-id": customerId,
            };
        try {
            return await this.postWithHeaders("/v2/parcel", parcelRequest, {}, headers);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async cancelParcel(customerId, cancelParcelRequest) {
        try {
            return await this.postWithHeaders(`/v2/parcel/cancel`, cancelParcelRequest, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async cancelParcels(customerId, cancelParcelsRequest) {
        try {
            return await this.postWithHeaders(`/v2/parcel/cancel/batch`, cancelParcelsRequest, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async cancelParcelsCsv(customerId, csvContentFormData) {
        try {
            // Handling the case where this is called on a Browser vs Node environment
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headers = csvContentFormData.getHeaders?.() ?? {};
            return await this.postWithHeaders("/v2/parcel/bulk-parcel-cancellation", csvContentFormData, {}, {
                ...headers,
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getParcel(customerId, parcelId) {
        try {
            return await this.get(`/v2/parcel/${parcelId}`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getParcelTracking(customerId, parcelId) {
        try {
            return await this.get(`/v2/parcel/${parcelId}/tracking`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getParcelLabel(labelId) {
        try {
            return await this.get(`/v2/parcel/label/${labelId}`, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getParcelTrackingByTrackingCode(customerId, trackingCode) {
        try {
            return await this.get(`/v2/parcel/tracking/${trackingCode}`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getTrackingUrl(customerId, parcelId) {
        try {
            return await this.get(`/v2/parcel/tracking-url/${parcelId}`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getQuote(customerId, quote) {
        try {
            return await this.postWithHeaders("/v2/parcel/quote", quote, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async determinePricingSheet(csvContentFormData) {
        try {
            // Handling the case where this is called on a Browser vs Node environment
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headers = csvContentFormData.getHeaders?.() ?? {};
            return await this.postWithHeaders("/v1/pricing/pld", csvContentFormData, {}, headers);
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getCostCalculatorQuote(quote) {
        try {
            return await this.postWithHeaders("/v3/parcel/quote", quote, {}, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getBulkParcelJob(customerId, id) {
        try {
            return await this.get(`/v1/bulkParcelJobs/${id}`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async createBulkParcelJob(customerId, bulkParcelJobRequest) {
        try {
            return await this.postWithHeaders(`/v1/bulkParcelJobs`, bulkParcelJobRequest, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async uploadParcelCsv(customerId, csvContentFormData) {
        try {
            // Handling the case where this is called on a Browser vs Node environment
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headers = csvContentFormData.getHeaders?.() ?? {};
            return await this.postWithHeaders("/v1/bulkParcelJobs/uploadCsv", csvContentFormData, {}, {
                ...headers,
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async syncUploadParcelCsv(customerId, csvContentFormData) {
        try {
            // Handling the case where this is called on a Browser vs Node environment
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headers = csvContentFormData.getHeaders?.() ?? {};
            return await this.postWithHeaders("/v1/bulkParcelJobs/syncUploadCsv", csvContentFormData, {}, {
                ...headers,
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async updateBulkParcelJob(updateBulkParcelJobRequest) {
        try {
            return await this.post(`/v1/bulkParcelJobs/update`, updateBulkParcelJobRequest, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getOpenSearchParcelDetails(openSearchParcelDetailsRequest) {
        try {
            return await this.post("/opensearch/backfill", openSearchParcelDetailsRequest, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getParcelDetails(customerId, parcelId) {
        try {
            return await this.get(`/parcel-details/${parcelId}`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async isParcelIntegrationUser(customerId) {
        try {
            return await this.post(`/v2/parcel/isParcelIntegrationUser`, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async linkFlexId(linkFlexIdRequest) {
        try {
            return await this.postWithHeaders(`/v2/parcel/link-flexId`, linkFlexIdRequest, {}, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async linkFlexIds(customerId, csvContentFormData) {
        try {
            // Handling the case where this is called on a Browser vs Node environment
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const headers = csvContentFormData.getHeaders?.() ?? {};
            return await this.postWithHeaders("/v2/parcel/bulk-link-flexId", csvContentFormData, {}, {
                ...headers,
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getLabelContentsByTrackingCode(trackingCode) {
        try {
            return await this.get(`/v2/parcel/ops/trackingCode/${trackingCode}/labelContents`, {}, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getTrackingEvents(trackingCode) {
        try {
            return await this.get(`/v2/parcel/tracking-events/${trackingCode}`, {}, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getPublicTrackingEvents(trackingCode) {
        try {
            return await this.get(`/v2/parcel/public/tracking-events/${trackingCode}`, {}, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async getTrackingStatusOps(trackingCode) {
        try {
            return await this.get(`/v2/parcel/ops/tracking/${trackingCode}`, {});
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async createParcelWithOpsOverrides(customerId, parcelRequest, parcelOverrides) {
        try {
            const requestBody = {
                parcelRequest,
                parcelOverrides,
            };
            return await this.postWithHeaders(`/v2/parcel/ops`, requestBody, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async repurchaseParcel(customerId, parcelId, repurchaseParcel) {
        try {
            return await this.postWithHeaders(`/v2/parcel/${parcelId}/repurchase`, repurchaseParcel, {}, {
                "x-customer-id": customerId,
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
    async createMasterAirwayBillJob(masterAirwayBill) {
        try {
            return await this.postWithHeaders(`/customs/create-master-airway-bill-job`, masterAirwayBill, {}, {
                "Content-Type": "application/json; charset=utf-8",
            });
        }
        catch (err) {
            throw (0, base_client_1.mapError)(err);
        }
    }
}
exports.ParcelClient = ParcelClient;
//# sourceMappingURL=ParcelClient.js.map